import { useEffect, useState } from "react";
import type { SessionInfo } from "../shared/types.ts";
import { api } from "./api.ts";
import { Loading } from "./components.tsx";
import { Archive } from "./pages/Archive.tsx";
import { BulkAdd } from "./pages/BulkAdd.tsx";
import { Login } from "./pages/Login.tsx";
import { ProjectDetail } from "./pages/ProjectDetail.tsx";
import { ProjectList } from "./pages/ProjectList.tsx";
import { QuickAdd } from "./pages/QuickAdd.tsx";
import { matchPath, navigate, useRoute } from "./useRoute.ts";

export function App() {
  // undefined = still checking, null = signed out
  const [session, setSession] = useState<SessionInfo | null | undefined>(undefined);

  useEffect(() => {
    api
      .me()
      .then(setSession)
      .catch(() => setSession(null));
  }, []);

  if (session === undefined) return <Loading label="Starting up…" />;
  if (session === null) return <Login onSignedIn={setSession} />;

  return <Shell session={session} onSignedOut={() => setSession(null)} />;
}

function Shell({ session, onSignedOut }: { session: SessionInfo; onSignedOut: () => void }) {
  const path = useRoute();

  async function signOut() {
    try {
      await api.logout();
    } finally {
      onSignedOut();
      navigate("/");
    }
  }

  return (
    <div className="app">
      <header className="app-header">
        <button className="brand" onClick={() => navigate("/")}>
          <span className="brand-mark">✓</span> Honeydo
        </button>
        <div className="app-header-right">
          <span className="muted small">{session.person.name}</span>
          <button className="link" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      <main className="app-main">
        <Routes session={session} path={path} />
      </main>

      <nav className="tabbar">
        <Tab path={path} to="/" label="List" icon="☰" />
        <Tab path={path} to="/add" label="Add" icon="＋" />
        <Tab path={path} to="/archive" label="Done" icon="✓" />
      </nav>
    </div>
  );
}

function Routes({ session, path }: { session: SessionInfo; path: string }) {
  const detail = matchPath("/p/:id", path);
  if (detail) return <ProjectDetail id={detail.id!} />;

  switch (path.split("?")[0]) {
    case "/add":
      return <QuickAdd />;
    case "/bulk-add":
      return <BulkAdd />;
    case "/archive":
      return <Archive />;
    case "/":
      return <ProjectList person={session.person} />;
    default:
      return (
        <p className="muted center pad">
          Nothing here. <a href="#/">Back to the list →</a>
        </p>
      );
  }
}

function Tab({ path, to, label, icon }: { path: string; to: string; label: string; icon: string }) {
  const active = path === to || (to === "/" && path.startsWith("/p/"));
  return (
    <button className={`tab ${active ? "tab-on" : ""}`} onClick={() => navigate(to)}>
      <span className="tab-icon" aria-hidden="true">
        {icon}
      </span>
      {label}
    </button>
  );
}
